import React from "react";
import { Alert, Box, Typography } from "@mui/material";
import {
  HaloPermissionFeedback,
  haloPermissionStatusLabel,
} from "../utils/haloPermissionChecks";

type HaloPermissionFeedbackAlertProps = {
  feedback: HaloPermissionFeedback;
  connectionMessage?: string;
  onClose?: () => void;
};

/**
 * Shared Halo permission-check result alert used across onboarding, add-integration,
 * and integration-configuration pages to keep presentation consistent.
 */
const HaloPermissionFeedbackAlert: React.FC<HaloPermissionFeedbackAlertProps> = ({
  feedback,
  connectionMessage,
  onClose,
}) => {
  const failedByPermission = new Set(
    (feedback.permissionChecks || [])
      .filter((p) => p.ok === false)
      .map((p) => p.permission)
  );
  const endpointToPermission: Record<string, string> = {
    "GET tickets (read:tickets)": "read:tickets",
    "GET client (read:customers)": "read:customers",
    "GET item (read:items)": "read:items",
    "GET appointment (read:calendar)": "read:calendar",
  };
  const nonRedundantFailedChecks = (feedback.failedChecks || []).filter((check) => {
    const mappedPermission = endpointToPermission[check.label];
    return mappedPermission ? !failedByPermission.has(mappedPermission) : true;
  });

  return (
    <Alert
      severity={feedback.success ? "success" : "error"}
      onClose={onClose}
      sx={{ mt: 2, whiteSpace: "pre-line" }}
    >
      {connectionMessage ? (
        <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, mb: 0.5 }}>
          {connectionMessage}
        </Typography>
      ) : null}
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, mb: 0.5 }}>
        {feedback.message}
      </Typography>
      {feedback.missingScopes.length > 0 ? (
        <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, mb: 0.5 }}>
          Missing scopes: {feedback.missingScopes.join(", ")}
        </Typography>
      ) : null}
      {feedback.permissionChecks.length > 0 ? (
        <Box component="ul" sx={{ m: 0, mb: 0.5, pl: 2.5 }}>
          {feedback.permissionChecks.map((check, idx) => (
            <Typography
              key={`${check.permission}-${idx}`}
              component="li"
              sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13 }}
            >
              <strong>{haloPermissionStatusLabel(check)}</strong> {check.permission}
              {check.reason ? ` — ${check.reason}` : ""}
            </Typography>
          ))}
        </Box>
      ) : null}
      {nonRedundantFailedChecks.length > 0 ? (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {nonRedundantFailedChecks.map((check, idx) => (
            <Typography
              key={`${check.label}-${idx}`}
              component="li"
              sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13 }}
            >
              {check.label}
              {check.status ? ` (HTTP ${check.status})` : ""}: {check.error}
            </Typography>
          ))}
        </Box>
      ) : null}
      {feedback.success &&
      feedback.missingScopes.length === 0 &&
      feedback.failedChecks.length === 0 ? (
        <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13 }}>
          All required Halo permissions are present and endpoint checks passed.
        </Typography>
      ) : null}
    </Alert>
  );
};

export default HaloPermissionFeedbackAlert;
